/**
 * TypedDiagram — generic React Flow wrapper consumed by every CSK demo
 * that renders a node-edge graph.
 *
 * Single responsibility: take a typed `nodes` + `edges` description, run
 * the deterministic `layeredLayout` to produce coordinates, and render the
 * result through `@xyflow/react`. The wrapper insulates demos from React
 * Flow's imperative store and edge / node APIs so consumers only express
 * the graph data they own.
 *
 * Why typed:
 *   - `TKind extends string` lets each specialization (provenance, seam
 *     bridge, attack surface) constrain `kind` to its own vocabulary,
 *     so a misnamed node category is a compile error, not a runtime
 *     "unknown" fall-through.
 *   - Coordinates are computed by `layeredLayout` from the typed input;
 *     no manual `position: { x, y }` fiddling per node — satisfying the
 *     acceptance criterion that layout be "reproducible from typed input
 *     data only".
 *
 * Why not elkjs by default:
 *   - elkjs is asynchronous (Web Worker / Promise) and adds ~250 KB to
 *     the bundle. The CSK demos render small DAGs (≤ ~20 nodes), so the
 *     synchronous deterministic layout shipped here matches the legacy
 *     `ProvenanceGraph` exactly and stays snapshot-stable. Consumers
 *     that need force-directed or hierarchical layout can pre-position
 *     their nodes (via elkjs in a parent effect) and pass the positioned
 *     nodes through; `TypedDiagram` will honor explicit `position` when
 *     it is supplied.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  Background,
  Controls,
  type Edge,
  type EdgeMarker,
  MarkerType,
  type Node,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import { DEFAULT_NODE_HEIGHT, DEFAULT_NODE_WIDTH, layeredLayout } from "./layeredLayout.js";
import { GRAY } from "../../design-system/tokens.js";
import { cn } from "../../styling/index.js";

// React Flow ships its own CSS; consumers must arrange for it to be
// bundled. We do not import the stylesheet here because library
// builds do not co-bundle CSS — the host app's CSS pipeline imports
// `@xyflow/react/dist/style.css` (see `demos/app/src/styles/globals.css`).

/**
 * A diagram node parameterised by the kind vocabulary it belongs to.
 *
 * `kind` is a discriminator the consumer interprets — `TypedDiagram`
 * passes it through to the renderer so callers can color, shape, or
 * label-prefix per kind. `width` / `height` override the layout defaults
 * for that single node; omit them to inherit the columnar grid.
 */
export interface TypedDiagramNode<TKind extends string = string> {
  readonly id: string;
  readonly kind: TKind;
  readonly label: string;
  /** Optional secondary text, rendered under the label. */
  readonly sublabel?: string;
  /**
   * Override the render size of this node only. Column / row spacing
   * is still computed from the layout defaults — large overrides may
   * overlap neighbors. See `layeredLayout` for the trade-off.
   */
  readonly width?: number;
  /** See `width`. */
  readonly height?: number;
  /** Explicit coordinates; when set, `layeredLayout` does not move this node. */
  readonly position?: { readonly x: number; readonly y: number };
}

export interface TypedDiagramEdge {
  readonly source: string;
  readonly target: string;
  readonly label?: string;
}

/**
 * Style for a single node kind. The renderer pulls these from a typed
 * record so a missing kind is a compile error at the specialization
 * (e.g., `ProvenanceDiagram`) layer.
 */
export interface TypedDiagramNodeStyle {
  /** Background fill for the node body. */
  readonly fill: string;
  /** Border stroke. */
  readonly border: string;
  /** Foreground text color. */
  readonly text: string;
}

export interface TypedDiagramProps<TKind extends string> {
  readonly nodes: readonly TypedDiagramNode<TKind>[];
  readonly edges: readonly TypedDiagramEdge[];
  /** Per-kind style record — required so visual treatment is type-safe. */
  readonly nodeStyles: Readonly<Record<TKind, TypedDiagramNodeStyle>>;
  /** Accessible label for the diagram region. */
  readonly ariaLabel: string;
  /** Optional figcaption rendered below the diagram. */
  readonly caption?: string;
  /** Override the rendered height in CSS pixels (default 320). */
  readonly height?: number;
  /** Additional Tailwind class names appended to the outer figure. */
  readonly className?: string;
}

interface TypedNodeData<TKind extends string> {
  readonly label: string;
  readonly sublabel?: string;
  readonly kind: TKind;
  readonly style: TypedDiagramNodeStyle;
  readonly [key: string]: unknown;
}

const EDGE_MARKER: EdgeMarker = { type: MarkerType.ArrowClosed, color: GRAY[500] };
const EDGE_STYLE: React.CSSProperties = { stroke: GRAY[500], strokeWidth: 1.5 };
const EDGE_LABEL_STYLE: React.CSSProperties = { fontSize: 10, fill: GRAY[600] };
const FIT_VIEW_OPTIONS = { padding: 0.15 };
const PRO_OPTIONS = { hideAttribution: true };

function TypedDiagramNodeRenderer<TKind extends string>({
  data,
}: {
  readonly data: TypedNodeData<TKind>;
}): React.JSX.Element {
  const { label, sublabel, kind, style } = data;
  return (
    <div
      data-kind={kind}
      style={{
        backgroundColor: style.fill,
        border: `1px solid ${style.border}`,
        color: style.text,
        borderRadius: 6,
        padding: "6px 10px",
        textAlign: "center",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.2,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      <div>{label}</div>
      {sublabel !== undefined && (
        <div style={{ fontWeight: 400, fontSize: 9, color: GRAY[600], marginTop: 2 }}>
          {sublabel}
        </div>
      )}
    </div>
  );
}

/**
 * Render `nodes` and `edges` as an interactive React Flow graph.
 *
 * Coordinates come from `layeredLayout` for nodes that don't specify
 * their own `position`. Each node is painted with the kind-keyed style
 * from `nodeStyles`. Controls (zoom, fit-view) are rendered in the
 * bottom-right; the background is a subtle dot grid.
 */
export function TypedDiagram<TKind extends string>({
  nodes,
  edges,
  nodeStyles,
  ariaLabel,
  caption,
  height = 320,
  className,
}: TypedDiagramProps<TKind>): React.JSX.Element {
  const positioned = React.useMemo(() => layeredLayout(nodes, edges), [nodes, edges]);

  const flowNodes = React.useMemo<Node<TypedNodeData<TKind>>[]>(() => {
    // `positioned` is computed from `nodes`, so every node id is present
    // — we can index the map without a `?.` fallback.
    const positionsById = new Map<string, (typeof positioned)[number]>();
    for (const placement of positioned) positionsById.set(placement.id, placement);
    const out: Node<TypedNodeData<TKind>>[] = [];
    for (const node of nodes) {
      const placement = positionsById.get(node.id) ?? {
        x: 0,
        y: 0,
        width: DEFAULT_NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
      };
      // `node.position`, when set, overrides the deterministic layout
      // — useful for callers that pre-position from elkjs or similar.
      const position = node.position ?? { x: placement.x, y: placement.y };
      const style = nodeStyles[node.kind];
      const data: TypedNodeData<TKind> =
        node.sublabel === undefined
          ? { label: node.label, kind: node.kind, style }
          : { label: node.label, sublabel: node.sublabel, kind: node.kind, style };
      const flowNode: Node<TypedNodeData<TKind>> = {
        id: node.id,
        type: "typed",
        position: { x: position.x, y: position.y },
        width: placement.width,
        height: placement.height,
        data,
      };
      out.push(flowNode);
    }
    return out;
  }, [nodes, nodeStyles, positioned]);

  const flowEdges = React.useMemo<Edge[]>(() => {
    const knownIds = new Set<string>();
    for (const node of nodes) knownIds.add(node.id);
    const out: Edge[] = [];
    edges.forEach((edge, index) => {
      if (!knownIds.has(edge.source) || !knownIds.has(edge.target)) return;
      // The input index disambiguates parallel edges between the same
      // pair of endpoints — without it, React Flow would receive
      // duplicate `id`s and drop/overwrite the later edges.
      const flowEdge: Edge = {
        id: `${edge.source}->${edge.target}#${index}`,
        source: edge.source,
        target: edge.target,
        markerEnd: EDGE_MARKER,
        style: EDGE_STYLE,
        labelStyle: EDGE_LABEL_STYLE,
      };
      out.push(edge.label === undefined ? flowEdge : { ...flowEdge, label: edge.label });
    });
    return out;
  }, [edges, nodes]);

  const nodeTypes = React.useMemo(() => ({ typed: TypedDiagramNodeRenderer<TKind> }), []);

  return (
    <figure className={cn("m-0", className)}>
      <div
        role="img"
        aria-label={ariaLabel}
        style={{
          width: "100%",
          height,
          border: `1px solid ${GRAY[300]}`,
          borderRadius: 6,
          background: "white",
        }}
      >
        <ReactFlowProvider>
          <ReactFlow
            nodes={flowNodes}
            edges={flowEdges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={FIT_VIEW_OPTIONS}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={PRO_OPTIONS}
          >
            <Background gap={20} size={1} color={GRAY[300]} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
      {caption !== undefined && (
        <figcaption
          style={{
            color: GRAY[500],
            fontSize: "0.75rem",
            marginTop: "0.25rem",
            textAlign: "center",
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
